/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Utils
import { API_ESA_OFFICE } from "utils";
import { logoutUser } from "actions/AuthAction";

// MUI components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";

function DetailKinerjaBidang() {
  const { bidangId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [kinerjaData, setKinerjaData] = useState(null);
  const [pegawaiList, setPegawaiList] = useState([]);
  const [layananList, setLayananList] = useState([]);
  const [layananSurveyData, setLayananSurveyData] = useState([]);
  const [tamuList, setTamuList] = useState([]);
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [namaDinas, setNamaDinas] = useState("");
  const [selectedBulan, setSelectedBulan] = useState(new Date().getMonth() + 1);
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());

  // Generate tahun list (current year down to 2025)
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2025; i--) {
    tahunList.push(i);
  }

  // List bulan
  const bulanList = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserId(user.uid || user.user_id);
      setNamaDinas(user.nama_dinas || "");
    } else {
      logoutUser(navigate);
    }
  }, [navigate]);

  // Fungsi untuk menghitung tanggal awal dan akhir berdasarkan bulan dan tahun
  const getDateRange = (bulan, tahun) => {
    const startDate = `${tahun}-${String(bulan).padStart(2, "0")}-01`;
    const endDate = new Date(tahun, bulan, 0).toISOString().split("T")[0];
    return { startDate, endDate };
  };

  // Fungsi untuk menghitung hari kerja
  const calculateWorkingDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    let workingDays = 0;
    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const dayOfWeek = d.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  };

  // Fetch functions - menggunakan bidangId dari parameter
  const fetchKinerjaData = async (currentBidangId, bulan, tahun) => {
    try {
      console.log("🔍 Fetching kinerja data for bidangId:", currentBidangId);

      // Hitung tanggal berdasarkan bulan dan tahun yang dipilih
      const { startDate, endDate } = getDateRange(bulan, tahun);
      const hariKerja = calculateWorkingDays(startDate, endDate);

      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/bidang/${currentBidangId}/kinerja?bulan=${bulan}&tahun=${tahun}`
      );
      console.log("📦 Response kinerja data:", response.data);
      if (response.data) {
        const kinerjaData = response.data;

        // Ambil hari kerja yang benar dari endpoint apel (memperhitungkan hari libur)
        let hariKerjaApel = hariKerja; // Fallback ke hari kerja lokal jika gagal
        if (kinerjaData.pegawai_data?.pegawai?.length > 0) {
          try {
            // Ambil hari kerja dari endpoint apel untuk pegawai pertama (semua pegawai memiliki hari kerja yang sama)
            const firstPegawai = kinerjaData.pegawai_data.pegawai.find(
              (p) => p.uid_RSS
            );
            if (firstPegawai?.uid_RSS) {
              const apelResponse = await axios.get(
                `${API_ESA_OFFICE}kinerja-organisasi/individu/${firstPegawai.uid_RSS}/apel?bulan=${bulan}&tahun=${tahun}`
              );
              if (apelResponse.data?.hari_kerja) {
                hariKerjaApel = apelResponse.data.hari_kerja;
              }
            }
          } catch (error) {
            console.error(
              `Error fetching hari kerja from apel endpoint for bidang ${currentBidangId}:`,
              error
            );
            // Gunakan hari kerja lokal sebagai fallback
          }
        }

        // Hitung persentase kehadiran dari endpoint eksternal
        // Menggunakan hari kerja yang sama dengan apel (memperhitungkan hari libur)
        let persentaseKehadiran = 0;
        if (
          kinerjaData.pegawai_data?.pegawai?.length > 0 &&
          kinerjaData.pegawai_data?.nama_dinas
        ) {
          try {
            const kehadiranResponse = await axios.post(
              "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
              {
                tAwal: startDate,
                tAkhir: endDate,
                unitKerja: kinerjaData.pegawai_data.nama_dinas,
              },
              {
                headers: {
                  "Content-Type": "application/json",
                },
                timeout: 30000,
              }
            );

            if (
              kehadiranResponse.data.success === 1 &&
              kehadiranResponse.data.data
            ) {
              const uidRSSList = kinerjaData.pegawai_data.pegawai.map((p) =>
                String(p.uid_RSS || "")
              );

              // Filter data berdasarkan uid_RSS yang ada di bidang
              const dataBidang = kehadiranResponse.data.data.filter((item) => {
                const uid = String(item.uid || item.uid_RSS || "");
                return uidRSSList.some((rss) => String(rss) === uid);
              });

              if (dataBidang.length > 0) {
                // Hitung total hari hadir dari semua pegawai di bidang
                const totalHariHadir = dataBidang.reduce(
                  (sum, item) => sum + (parseInt(item.hadir) || 0),
                  0
                );

                // Hitung persentase kehadiran menggunakan hari kerja dari apel (memperhitungkan hari libur)
                const totalPegawai = dataBidang.length;
                if (hariKerjaApel > 0 && totalPegawai > 0) {
                  persentaseKehadiran = parseFloat(
                    (
                      (totalHariHadir / (hariKerjaApel * totalPegawai)) *
                      100
                    ).toFixed(2)
                  );
                }
              }
            }
          } catch (error) {
            console.error(
              `Error fetching kehadiran data for bidang ${currentBidangId}:`,
              error
            );
            // Jika error, persentase tetap 0
          }
        }

        // Update periode dengan bulan dan tahun yang dipilih
        // Gunakan hari kerja dari apel (memperhitungkan hari libur) untuk konsistensi
        kinerjaData.periode = {
          bulan: bulan,
          tahun: tahun,
          start_date: startDate,
          end_date: endDate,
          hari_kerja: hariKerjaApel, // Gunakan hari kerja yang benar (memperhitungkan hari libur)
        };

        // Hitung ulang persentase apel menggunakan hari kerja yang benar (memperhitungkan hari libur)
        // Konsisten dengan perhitungan individu: persentase = (apel_hadir / (hari_kerja * total_pegawai)) * 100
        if (kinerjaData.statistik && hariKerjaApel > 0) {
          const totalPegawaiApel = kinerjaData.statistik.total_pegawai || 0;

          // Hitung ulang persentase apel pagi menggunakan hari kerja yang benar
          if (
            kinerjaData.statistik.apel_pagi_hadir !== undefined &&
            totalPegawaiApel > 0
          ) {
            const expectedTotalApelPagi = hariKerjaApel * totalPegawaiApel;
            if (expectedTotalApelPagi > 0) {
              kinerjaData.statistik.persentase_apel_pagi = parseFloat(
                (
                  (kinerjaData.statistik.apel_pagi_hadir /
                    expectedTotalApelPagi) *
                  100
                ).toFixed(2)
              );
            } else {
              kinerjaData.statistik.persentase_apel_pagi = 0;
            }
          }

          // Hitung ulang persentase apel siang menggunakan hari kerja yang benar
          if (
            kinerjaData.statistik.apel_siang_hadir !== undefined &&
            totalPegawaiApel > 0
          ) {
            const expectedTotalApelSiang = hariKerjaApel * totalPegawaiApel;
            if (expectedTotalApelSiang > 0) {
              kinerjaData.statistik.persentase_apel_siang = parseFloat(
                (
                  (kinerjaData.statistik.apel_siang_hadir /
                    expectedTotalApelSiang) *
                  100
                ).toFixed(2)
              );
            } else {
              kinerjaData.statistik.persentase_apel_siang = 0;
            }
          }

          // Hitung ulang persentase laporan menggunakan hari kerja yang benar (memperhitungkan hari libur)
          // Konsisten dengan perhitungan kehadiran dan apel: persentase = (total_laporan / (hari_kerja * total_pegawai)) * 100
          const totalLaporan = kinerjaData.statistik.total_laporan || 0;
          if (totalLaporan !== undefined && totalPegawaiApel > 0) {
            const expectedTotalLaporan = hariKerjaApel * totalPegawaiApel;
            if (expectedTotalLaporan > 0) {
              kinerjaData.statistik.persentase_laporan = parseFloat(
                ((totalLaporan / expectedTotalLaporan) * 100).toFixed(2)
              );
            } else {
              kinerjaData.statistik.persentase_laporan = 0;
            }
          }
        }

        // Tambahkan persentase kehadiran ke statistik
        if (kinerjaData.statistik) {
          kinerjaData.statistik.persentase_kehadiran = persentaseKehadiran;
        }

        setKinerjaData(kinerjaData);
      } else {
        console.warn("⚠️ Response data is empty");
        setKinerjaData(null);
      }
    } catch (error) {
      console.error("❌ Error fetching kinerja data:", error);
      console.error("Error details:", error.response?.data || error.message);
      setKinerjaData(null);
      // Jangan tampilkan swal untuk setiap error, biarkan user melihat "Data tidak ditemukan"
    }
  };

  const fetchPegawai = async (currentBidangId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/bidang/${currentBidangId}/pegawai`
      );
      setPegawaiList(response.data || []);
    } catch (error) {
      console.error("Error fetching pegawai:", error);
    }
  };

  const fetchLayanan = async (currentBidangId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/bidang/${currentBidangId}/layanan-publik`
      );
      const layananData = response.data || [];
      setLayananList(layananData);

      // Fetch data survey kepuasan untuk setiap layanan
      if (layananData.length > 0) {
        const surveyPromises = layananData.map(async (layanan) => {
          try {
            const surveyResponse = await axios.get(
              `${API_ESA_OFFICE}survey-kepuasan/layanan-publik/rekap?tahun=${selectedTahun}`
            );
            const surveyData = surveyResponse.data || [];
            const layananSurvey = surveyData.find((s) => s.id === layanan.id);
            return {
              ...layanan,
              total_responden: layananSurvey?.total_responden || 0,
              rata_rata_nilai: layananSurvey?.rata_rata_nilai || 0,
              rata_rata_skor: layananSurvey?.rata_rata_skor || 0,
            };
          } catch (error) {
            console.error(
              `Error fetching survey data for layanan ${layanan.id}:`,
              error
            );
            return {
              ...layanan,
              total_responden: 0,
              rata_rata_nilai: 0,
              rata_rata_skor: 0,
            };
          }
        });

        const layananWithSurvey = await Promise.all(surveyPromises);
        setLayananSurveyData(layananWithSurvey);
      } else {
        setLayananSurveyData([]);
      }
    } catch (error) {
      console.error("Error fetching layanan:", error);
      setLayananSurveyData([]);
    }
  };

  const fetchTamu = async (currentBidangId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/bidang/${currentBidangId}/tamu`
      );
      setTamuList(response.data || []);
    } catch (error) {
      console.error("Error fetching tamu:", error);
    }
  };

  useEffect(() => {
    // Reset semua state saat bidangId atau location berubah
    if (!bidangId) {
      setLoading(false);
      return;
    }

    console.log(
      "🔄 useEffect triggered - bidangId:",
      bidangId,
      "dinasId:",
      dinasId,
      "pathname:",
      location.pathname
    );

    setLoading(true);
    setKinerjaData(null);
    setPegawaiList([]);
    setLayananList([]);
    setTamuList([]);

    // Tunggu sampai dinasId tersedia
    if (!dinasId) {
      setLoading(false);
      return;
    }

    // Fetch semua data secara paralel dengan bidangId yang terbaru
    const fetchAllData = async () => {
      try {
        console.log("📡 Fetching data for bidangId:", bidangId);
        await Promise.all([
          fetchKinerjaData(bidangId, selectedBulan, selectedTahun),
          fetchPegawai(bidangId),
          fetchLayanan(bidangId),
          fetchTamu(bidangId),
        ]);
        console.log("✅ Data fetched successfully for bidangId:", bidangId);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [bidangId, dinasId, location.pathname]);

  // Fetch ulang data kinerja saat bulan atau tahun berubah
  useEffect(() => {
    if (bidangId && dinasId && !loading) {
      setLoading(true);
      fetchKinerjaData(bidangId, selectedBulan, selectedTahun).finally(() => {
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBulan, selectedTahun]);

  // Fetch ulang data layanan saat tahun berubah
  useEffect(() => {
    if (bidangId) {
      fetchLayanan(bidangId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTahun]);

  // Fetch kehadiran bulanan menggunakan endpoint yang sama dengan smartDH/kinerja/index.js
  const fetchKehadiranBulanan = async (uid_RSS, bulan, tahun, unitKerja) => {
    try {
      // Hitung tanggal awal dan akhir bulan
      const startDate = `${tahun}-${String(bulan).padStart(2, "0")}-01`;
      const endDate = new Date(tahun, bulan, 0).toISOString().split("T")[0];

      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
        {
          tAwal: startDate,
          tAkhir: endDate,
          unitKerja: unitKerja,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      );

      if (response.data.success === 1 && response.data.data) {
        // Filter berdasarkan uid_RSS
        const userData = response.data.data.find(
          (item) => String(item.uid || item.uid_RSS || "") === String(uid_RSS)
        );

        if (userData) {
          // Hitung hari kerja (exclude weekend)
          const calculateWorkingDays = (start, end) => {
            const startDate = new Date(start);
            const endDate = new Date(end);
            let workingDays = 0;
            for (
              let d = new Date(startDate);
              d <= endDate;
              d.setDate(d.getDate() + 1)
            ) {
              const dayOfWeek = d.getDay();
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                workingDays++;
              }
            }
            return workingDays;
          };

          const hariKerja = calculateWorkingDays(startDate, endDate);

          return {
            hadir: parseInt(userData.hadir) || 0,
            hariKerja: hariKerja,
          };
        }
      }
      return null;
    } catch (error) {
      console.error("Error fetching kehadiran bulanan:", error);
      return null;
    }
  };

  // Fetch apel untuk current month
  const fetchApel = async (uid_RSS) => {
    try {
      const { startDate, endDate } = getDateRange(selectedBulan, selectedTahun);
      const hariKerja = calculateWorkingDays(startDate, endDate);

      // Menggunakan endpoint statistik apel per individu
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/individu/${uid_RSS}/apel?bulan=${selectedBulan}&tahun=${selectedTahun}`
      );

      if (response.data) {
        return {
          apel_pagi_hadir: response.data.apel_pagi_hadir || 0,
          persentase_apel_pagi: response.data.persentase_apel_pagi || 0,
          apel_siang_hadir: response.data.apel_siang_hadir || 0,
          persentase_apel_siang: response.data.persentase_apel_siang || 0,
          hari_kerja: response.data.hari_kerja || hariKerja,
        };
      }
      return {
        apel_pagi_hadir: 0,
        persentase_apel_pagi: 0,
        apel_siang_hadir: 0,
        persentase_apel_siang: 0,
        hari_kerja: hariKerja,
      };
    } catch (error) {
      console.error("Error fetching apel:", error);
      const { startDate, endDate } = getDateRange(selectedBulan, selectedTahun);
      const hariKerja = calculateWorkingDays(startDate, endDate);
      return {
        apel_pagi_hadir: 0,
        persentase_apel_pagi: 0,
        apel_siang_hadir: 0,
        persentase_apel_siang: 0,
        hari_kerja: hariKerja,
      };
    }
  };

  // Fetch laporan harian untuk current month
  const fetchLaporanHarian = async (uid_RSS) => {
    try {
      const { startDate, endDate } = getDateRange(selectedBulan, selectedTahun);
      let hariKerja = calculateWorkingDays(startDate, endDate); // Fallback

      // Ambil hari kerja dari endpoint apel (memperhitungkan hari libur)
      // Menggunakan hari kerja yang sama dengan perhitungan persentase kehadiran
      try {
        const apelResponse = await axios.get(
          `${API_ESA_OFFICE}kinerja-organisasi/individu/${uid_RSS}/apel?bulan=${selectedBulan}&tahun=${selectedTahun}`
        );
        if (apelResponse.data?.hari_kerja) {
          hariKerja = apelResponse.data.hari_kerja;
        }
      } catch (error) {
        console.error(
          `Error fetching hari kerja from apel endpoint for ${uid_RSS}:`,
          error
        );
        // Gunakan hari kerja lokal sebagai fallback
      }

      // Menggunakan endpoint statistik pegawai dengan filter bulan dan tahun
      const response = await axios.get(
        `${API_ESA_OFFICE}laporan-harian/statistik/pegawai/${uid_RSS}`,
        {
          params: {
            bulan: selectedBulan,
            tahun: selectedTahun,
          },
        }
      );

      if (response.data) {
        const totalLaporan = response.data.total_laporan || 0;
        const persentaseLaporan =
          hariKerja > 0
            ? parseFloat(((totalLaporan / hariKerja) * 100).toFixed(2))
            : 0;

        return {
          total_laporan: totalLaporan,
          hari_kerja: hariKerja,
          persentase: persentaseLaporan,
        };
      }
      return { total_laporan: 0, hari_kerja: hariKerja, persentase: 0 };
    } catch (error) {
      console.error("Error fetching laporan harian:", error);
      const { startDate, endDate } = getDateRange(selectedBulan, selectedTahun);
      const hariKerja = calculateWorkingDays(startDate, endDate);
      return { total_laporan: 0, hari_kerja: hariKerja, persentase: 0 };
    }
  };

  // Fetch statistik disposisi dan instruksi per individu
  const fetchStatistikIndividu = async (userId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/individu/${userId}/statistik?bulan=${selectedBulan}&tahun=${selectedTahun}`
      );
      return (
        response.data || {
          total_disposisi_diterima: 0,
          total_disposisi_selesai: 0,
          persentase_disposisi: 0,
          total_instruksi_diterima: 0,
          total_instruksi_selesai: 0,
          persentase_instruksi: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching statistik individu:", error);
      return {
        total_disposisi_diterima: 0,
        total_disposisi_selesai: 0,
        persentase_disposisi: 0,
        total_instruksi_diterima: 0,
        total_instruksi_selesai: 0,
        persentase_instruksi: 0,
      };
    }
  };

  // Fetch disposisi untuk current month (deprecated, gunakan fetchStatistikIndividu)
  const fetchDisposisi = async (userId) => {
    try {
      const statistik = await fetchStatistikIndividu(userId);
      return {
        diterima: statistik.total_disposisi_diterima,
        selesai: statistik.total_disposisi_selesai,
        persentase: statistik.persentase_disposisi,
      };
    } catch (error) {
      console.error("Error fetching disposisi:", error);
      return { diterima: 0, selesai: 0, persentase: 0 };
    }
  };

  // Fetch instruksi untuk current month (deprecated, gunakan fetchStatistikIndividu)
  const fetchInstruksi = async (userId) => {
    try {
      const statistik = await fetchStatistikIndividu(userId);
      return {
        diterima: statistik.total_instruksi_diterima,
        selesai: statistik.total_instruksi_selesai,
        persentase: statistik.persentase_instruksi,
      };
    } catch (error) {
      console.error("Error fetching instruksi:", error);
      return { diterima: 0, selesai: 0, persentase: 0 };
    }
  };

  // Fetch jumlah event yang diikuti per individu (uid_RSS)
  const fetchEventCount = async (uid_RSS) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/individu/${uid_RSS}/event-count?bulan=${selectedBulan}&tahun=${selectedTahun}`
      );
      return response.data.jumlah_event || 0;
    } catch (error) {
      console.error("Error fetching event count:", error);
      return 0;
    }
  };

  // Fetch statistik event per individu (user_id)
  // Mengembalikan: event yang dibuat, tamu dalam event yang dibuat, event yang dihadiri
  const fetchEventStatistik = async (user_id) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/individu/${user_id}/event-statistik?bulan=${selectedBulan}&tahun=${selectedTahun}`
      );
      return {
        jumlah_event_dibuat: response.data.jumlah_event_dibuat || 0,
        jumlah_tamu_event: response.data.jumlah_tamu_event || 0,
        jumlah_event_dihadiri: response.data.jumlah_event_dihadiri || 0,
      };
    } catch (error) {
      console.error("Error fetching event statistik:", error);
      return {
        jumlah_event_dibuat: 0,
        jumlah_tamu_event: 0,
        jumlah_event_dihadiri: 0,
      };
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <MDTypography variant="body2" color="text" mt={2}>
            Memuat data...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!kinerjaData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <MDTypography variant="h5">Data tidak ditemukan</MDTypography>
          <MDTypography variant="body2" color="text" mt={1}>
            Bidang ID: {bidangId}
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Dinas ID: {dinasId}
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Pathname: {location.pathname}
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const { bidang, statistik, periode } = kinerjaData;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <MDTypography variant="h4" fontWeight="bold">
              Detail Kinerja Bidang - {bidang.nama_bidang}
            </MDTypography>
            <MDBox display="flex" gap={2} alignItems="center">
              <Autocomplete
                options={bulanList}
                value={bulanList.find((b) => b.value === selectedBulan) || null}
                onChange={(event, newValue) => {
                  if (newValue) setSelectedBulan(newValue.value);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Bulan"
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      minWidth: 150,
                    }}
                  />
                )}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
              />
              <Autocomplete
                options={tahunList}
                value={selectedTahun}
                onChange={(event, newValue) => {
                  if (newValue) setSelectedTahun(newValue);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Tahun"
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      minWidth: 120,
                    }}
                  />
                )}
                getOptionLabel={(option) => option.toString()}
              />
            </MDBox>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text">
              Periode: {bulanList.find((b) => b.value === selectedBulan)?.label}{" "}
              {selectedTahun}
            </MDTypography>
          </MDBox>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => navigate("/kinerja-organisasi/kinerja-bidang")}
            >
              <Icon fontSize="small">arrow_back</Icon>
              &nbsp;Kembali
            </MDButton>
          </MDBox>
          <MDTypography variant="body2" color="text">
            Periode:{" "}
            {new Date(periode.start_date).toLocaleDateString("id-ID", {
              month: "long",
              year: "numeric",
            })}
          </MDTypography>
        </MDBox>

        {/* Statistik Agregat Bidang - Card Layout */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(0, 123, 255, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" color="info">
                {statistik.total_pegawai || 0}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Pegawai
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(40, 167, 69, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" color="success">
                {statistik.total_layanan || 0}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Layanan
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(255, 193, 7, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" color="warning">
                {statistik.total_tamu || 0}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Tamu
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(220, 53, 69, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography
                variant="h5"
                fontWeight="bold"
                color={
                  (statistik.persentase_disposisi || 0) >= 80
                    ? "success"
                    : (statistik.persentase_disposisi || 0) >= 60
                    ? "warning"
                    : "error"
                }
              >
                {statistik.persentase_disposisi !== undefined
                  ? `${statistik.persentase_disposisi}%`
                  : "0%"}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mb={0.5}
              >
                Persentase Disposisi
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                ({statistik.total_disposisi_selesai || 0}/
                {statistik.total_disposisi || 0})
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(108, 117, 125, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography
                variant="h5"
                fontWeight="bold"
                color={
                  (statistik.persentase_instruksi || 0) >= 80
                    ? "success"
                    : (statistik.persentase_instruksi || 0) >= 60
                    ? "warning"
                    : "error"
                }
              >
                {statistik.persentase_instruksi !== undefined
                  ? `${statistik.persentase_instruksi}%`
                  : "0%"}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mb={0.5}
              >
                Persentase Instruksi/Tugas
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                ({statistik.total_instruksi_selesai || 0}/
                {statistik.total_instruksi || 0})
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(138, 43, 226, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" color="info">
                {statistik.total_event || 0}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mb={0.5}
              >
                Event
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                {statistik.total_peserta_event || 0} peserta
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(23, 162, 184, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" color="info">
                {statistik.persentase_kehadiran
                  ? `${statistik.persentase_kehadiran.toFixed(2)}%`
                  : "0.00%"}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Persentase Kehadiran
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(102, 187, 106, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" color="success">
                {statistik.persentase_apel_pagi
                  ? `${statistik.persentase_apel_pagi.toFixed(2)}%`
                  : "0.00%"}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mb={0.5}
              >
                Persentase Apel
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                (P: {statistik.persentase_apel_pagi?.toFixed(2) || "0.00"}% / S:{" "}
                {statistik.persentase_apel_siang?.toFixed(2) || "0.00"}%)
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MDBox
              p={1.5}
              borderRadius="lg"
              sx={{
                backgroundColor: "rgba(156, 39, 176, 0.1)",
                textAlign: "center",
              }}
            >
              <MDTypography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "#9c27b0" }}
              >
                {statistik.persentase_laporan
                  ? `${statistik.persentase_laporan.toFixed(2)}%`
                  : "0.00%"}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Persentase Laporan
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>

        {/* Info Bidang - Card Layout */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <MDBox
              display="flex"
              alignItems="center"
              mb={2}
              pb={2}
              sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}
            >
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ mr: 2 }}
              >
                {bidang.uid_RSS ? (
                  <>
                    <img
                      src={`https://smartdh.rotendaokab.go.id/uploads/${bidang.uid_RSS}.jpg`}
                      alt={bidang.nama_kepala || "Kepala Bidang"}
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #e0e0e0",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = "flex";
                        }
                      }}
                    />
                    <MDBox
                      display="none"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "grey.200",
                        color: "grey.500",
                      }}
                    >
                      <Icon sx={{ fontSize: 32 }}>person</Icon>
                    </MDBox>
                  </>
                ) : (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "grey.200",
                      color: "grey.500",
                    }}
                  >
                    <Icon sx={{ fontSize: 32 }}>person</Icon>
                  </MDBox>
                )}
              </MDBox>
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold">
                  {bidang.nama_bidang}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {bidang.nama_dinas}
                </MDTypography>
                {bidang.nama_kepala && (
                  <MDTypography variant="body2" color="text">
                    Kepala: {bidang.nama_kepala}
                  </MDTypography>
                )}
              </MDBox>
            </MDBox>
          </CardContent>
        </Card>

        {/* Kinerja Pegawai - Card Layout */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <MDTypography variant="h6" mb={2}>
              Kinerja Pegawai
            </MDTypography>
            <Grid container spacing={2}>
              {pegawaiList.map((pegawai, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <KinerjaPegawaiCard
                    pegawai={pegawai}
                    bidangId={bidangId}
                    fetchKehadiranBulanan={fetchKehadiranBulanan}
                    fetchApel={fetchApel}
                    fetchLaporanHarian={fetchLaporanHarian}
                    fetchDisposisi={fetchDisposisi}
                    fetchInstruksi={fetchInstruksi}
                    fetchEventCount={fetchEventCount}
                    fetchEventStatistik={fetchEventStatistik}
                    selectedBulan={selectedBulan}
                    selectedTahun={selectedTahun}
                    namaDinas={
                      kinerjaData?.pegawai_data?.nama_dinas || namaDinas
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Layanan Publik - Rekapan Survey Kepuasan */}
        {layananSurveyData.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <MDTypography variant="h6" mb={2}>
                Layanan Publik - Survey Kepuasan ({selectedTahun})
              </MDTypography>
              <Grid container spacing={2}>
                {layananSurveyData.map((layanan, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Card variant="outlined">
                      <CardContent>
                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                          {layanan.layanan}
                        </MDTypography>
                        <MDBox mb={1}>
                          <MDTypography variant="body2" color="text" mb={0.5}>
                            <strong>Total Responden:</strong>{" "}
                            {layanan.total_responden || 0}
                          </MDTypography>
                          <MDTypography variant="body2" color="text" mb={0.5}>
                            <strong>Rata-rata Nilai:</strong>{" "}
                            {layanan.rata_rata_nilai
                              ? layanan.rata_rata_nilai.toFixed(2)
                              : "0.00"}
                          </MDTypography>
                          <MDTypography variant="body2" color="text" mb={1}>
                            <strong>Rata-rata Skor:</strong>{" "}
                            <span
                              style={{
                                color:
                                  layanan.rata_rata_skor >= 75
                                    ? "#28a745"
                                    : layanan.rata_rata_skor >= 50
                                    ? "#ffc107"
                                    : "#dc3545",
                                fontWeight: "bold",
                              }}
                            >
                              {layanan.rata_rata_skor
                                ? layanan.rata_rata_skor.toFixed(2)
                                : "0.00"}
                            </span>
                          </MDTypography>
                        </MDBox>
                        <Chip
                          label={layanan.status}
                          color={
                            layanan.status === "aktif" ? "success" : "default"
                          }
                          size="small"
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Rekapan Tamu */}
        {tamuList.length > 0 && (
          <Card>
            <CardContent>
              <MDTypography variant="h6" mb={2}>
                Rekapan Tamu ({periode.bulan}/{periode.tahun})
              </MDTypography>

              {/* Total Tamu */}
              <MDBox mb={3}>
                <Card
                  variant="outlined"
                  sx={{ backgroundColor: "rgba(0, 123, 255, 0.05)" }}
                >
                  <CardContent>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <MDTypography variant="h6" fontWeight="bold">
                        Total Tamu
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold" color="info">
                        {tamuList.length}
                      </MDTypography>
                    </MDBox>
                  </CardContent>
                </Card>
              </MDBox>

              {/* Rekapan Keperluan */}
              <MDBox mb={3}>
                <MDTypography variant="h6" mb={2}>
                  Rekapan Berdasarkan Keperluan
                </MDTypography>
                <Grid container spacing={2}>
                  {(() => {
                    const keperluanCount = {};
                    tamuList.forEach((tamu) => {
                      const keperluan = tamu.keperluan || "Tidak Diketahui";
                      keperluanCount[keperluan] =
                        (keperluanCount[keperluan] || 0) + 1;
                    });
                    return Object.entries(keperluanCount)
                      .sort((a, b) => b[1] - a[1])
                      .map(([keperluan, count], idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Card variant="outlined">
                            <CardContent>
                              <MDTypography
                                variant="body2"
                                color="text"
                                mb={0.5}
                              >
                                {keperluan}
                              </MDTypography>
                              <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                color="primary"
                              >
                                {count} tamu
                              </MDTypography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ));
                  })()}
                </Grid>
              </MDBox>

              {/* Rekapan Bertemu Dengan */}
              <MDBox>
                <MDTypography variant="h6" mb={2}>
                  Rekapan Berdasarkan Bertemu Dengan
                </MDTypography>
                <Grid container spacing={2}>
                  {(() => {
                    const bertemuCount = {};
                    tamuList.forEach((tamu) => {
                      const nama =
                        tamu.nama_bertemu_dengan || "Tidak Diketahui";
                      bertemuCount[nama] = (bertemuCount[nama] || 0) + 1;
                    });
                    return Object.entries(bertemuCount)
                      .sort((a, b) => b[1] - a[1])
                      .map(([nama, count], idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Card variant="outlined">
                            <CardContent>
                              <MDTypography
                                variant="body2"
                                color="text"
                                mb={0.5}
                              >
                                {nama}
                              </MDTypography>
                              <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                color="success"
                              >
                                {count} tamu
                              </MDTypography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ));
                  })()}
                </Grid>
              </MDBox>
            </CardContent>
          </Card>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

// Component untuk card kinerja pegawai
function KinerjaPegawaiCard({
  pegawai,
  bidangId,
  fetchKehadiranBulanan,
  fetchApel,
  fetchLaporanHarian,
  fetchDisposisi,
  fetchInstruksi,
  fetchEventCount,
  fetchEventStatistik,
  selectedBulan,
  selectedTahun,
  namaDinas,
}) {
  const navigate = useNavigate();
  const [kinerja, setKinerja] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadKinerja = async () => {
      setLoading(true);

      const [
        kehadiranData,
        apel,
        laporan,
        disposisi,
        instruksi,
        eventCount,
        eventStatistik,
      ] = await Promise.all([
        fetchKehadiranBulanan(
          pegawai.uid_RSS,
          selectedBulan,
          selectedTahun,
          namaDinas
        ),
        fetchApel(pegawai.uid_RSS),
        fetchLaporanHarian(pegawai.uid_RSS),
        fetchDisposisi(pegawai.user_id),
        fetchInstruksi(pegawai.user_id),
        fetchEventCount(pegawai.uid_RSS),
        fetchEventStatistik(pegawai.user_id),
      ]);

      // Hitung persentase kehadiran menggunakan hari kerja dari apel
      let persentaseKehadiran = 0;
      const hariKerjaKehadiran = apel?.hari_kerja || 0;
      if (kehadiranData && hariKerjaKehadiran > 0) {
        persentaseKehadiran = parseFloat(
          ((kehadiranData.hadir / hariKerjaKehadiran) * 100).toFixed(2)
        );
      }

      // Hitung persentase laporan
      let persentaseLaporan = 0;
      if (laporan && laporan.hari_kerja > 0) {
        persentaseLaporan = parseFloat(
          ((laporan.total_laporan / laporan.hari_kerja) * 100).toFixed(2)
        );
      }

      setKinerja({
        persentaseKehadiran: persentaseKehadiran,
        hadir: kehadiranData?.hadir || 0,
        hariKerja: kehadiranData?.hariKerja || 0,
        apel_pagi_hadir: apel?.apel_pagi_hadir || 0,
        persentase_apel_pagi: apel?.persentase_apel_pagi || 0,
        apel_siang_hadir: apel?.apel_siang_hadir || 0,
        persentase_apel_siang: apel?.persentase_apel_siang || 0,
        apel_hari_kerja: apel?.hari_kerja || 0,
        laporan: laporan?.total_laporan || 0,
        laporan_hari_kerja: laporan?.hari_kerja || 0,
        persentaseLaporan: persentaseLaporan,
        disposisi_diterima: disposisi?.diterima || 0,
        disposisi_selesai: disposisi?.selesai || 0,
        disposisi_persentase: disposisi?.persentase || 0,
        instruksi_diterima: instruksi?.diterima || 0,
        instruksi_selesai: instruksi?.selesai || 0,
        instruksi_persentase: instruksi?.persentase || 0,
        jumlah_event: eventCount || 0,
        jumlah_event_dibuat: eventStatistik?.jumlah_event_dibuat || 0,
        jumlah_tamu_event: eventStatistik?.jumlah_tamu_event || 0,
        jumlah_event_dihadiri: eventStatistik?.jumlah_event_dihadiri || 0,
      });
      setLoading(false);
    };

    if (pegawai.uid_RSS) {
      loadKinerja();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pegawai.uid_RSS,
    pegawai.user_id,
    selectedBulan,
    selectedTahun,
    namaDinas,
  ]);

  const isKepala = pegawai.urutan === 1;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: 2,
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <MDBox display="flex" alignItems="center" mb={2}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ mr: 2 }}
          >
            <img
              src={`https://smartdh.rotendaokab.go.id/uploads/${pegawai.uid_RSS}.jpg`}
              alt={pegawai.nama}
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #e0e0e0",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = "flex";
                }
              }}
            />
            <MDBox
              display="none"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "grey.200",
                color: "grey.500",
              }}
            >
              <Icon sx={{ fontSize: 28 }}>person</Icon>
            </MDBox>
          </MDBox>
          <MDBox flexGrow={1}>
            {isKepala && (
              <Chip
                label="Kepala"
                color="primary"
                size="small"
                sx={{ mb: 0.5 }}
              />
            )}
            <MDTypography variant="h6" fontWeight="bold">
              {pegawai.nama}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {pegawai.nama_jabatan}
            </MDTypography>
          </MDBox>
          <MDBox>
            <IconButton
              color="info"
              size="small"
              onClick={() => {
                navigate(
                  `/kinerja-organisasi/bidang/${bidangId}/individu/${pegawai.uid_RSS}/detail`
                );
              }}
              sx={{ ml: 1 }}
              title="Lihat Detail Per Hari"
            >
              <Icon fontSize="small">visibility</Icon>
            </IconButton>
          </MDBox>
        </MDBox>
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </MDBox>
        ) : (
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <MDBox
                p={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(40, 167, 69, 0.1)" }}
              >
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={0.5}
                >
                  Persentase Kehadiran
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      kinerja?.persentaseKehadiran >= 80
                        ? "success"
                        : kinerja?.persentaseKehadiran >= 60
                        ? "warning"
                        : "error"
                    }
                  >
                    {kinerja?.persentaseKehadiran || 0}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    ({kinerja?.hadir || 0}/{kinerja?.apel_hari_kerja || 0})
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox
                p={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(0, 123, 255, 0.1)" }}
              >
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={0.5}
                >
                  Persentase Apel
                </MDTypography>
                <MDBox display="flex" flexDirection="column" gap={0.5}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Pagi:
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontWeight="bold"
                      color={
                        (kinerja?.persentase_apel_pagi || 0) >= 80
                          ? "success"
                          : (kinerja?.persentase_apel_pagi || 0) >= 60
                          ? "warning"
                          : "error"
                      }
                    >
                      {kinerja?.persentase_apel_pagi || 0}%
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      ({kinerja?.apel_pagi_hadir || 0}/
                      {kinerja?.apel_hari_kerja || 0})
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Siang:
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontWeight="bold"
                      color={
                        (kinerja?.persentase_apel_siang || 0) >= 80
                          ? "success"
                          : (kinerja?.persentase_apel_siang || 0) >= 60
                          ? "warning"
                          : "error"
                      }
                    >
                      {kinerja?.persentase_apel_siang || 0}%
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      ({kinerja?.apel_siang_hadir || 0}/
                      {kinerja?.apel_hari_kerja || 0})
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox
                p={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(255, 193, 7, 0.1)" }}
              >
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={0.5}
                >
                  Persentase Laporan
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      (kinerja?.persentaseLaporan || 0) >= 80
                        ? "success"
                        : (kinerja?.persentaseLaporan || 0) >= 60
                        ? "warning"
                        : "error"
                    }
                  >
                    {kinerja?.persentaseLaporan || 0}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    ({kinerja?.laporan || 0}/{kinerja?.laporan_hari_kerja || 0})
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox
                p={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(220, 53, 69, 0.1)" }}
              >
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={0.5}
                >
                  Persentase Disposisi
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      (kinerja?.disposisi_persentase || 0) >= 80
                        ? "success"
                        : (kinerja?.disposisi_persentase || 0) >= 60
                        ? "warning"
                        : "error"
                    }
                  >
                    {kinerja?.disposisi_persentase || 0}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    ({kinerja?.disposisi_selesai || 0}/
                    {kinerja?.disposisi_diterima || 0})
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox
                p={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(108, 117, 125, 0.1)" }}
              >
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={0.5}
                >
                  Persentase Instruksi/Tugas
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      (kinerja?.instruksi_persentase || 0) >= 80
                        ? "success"
                        : (kinerja?.instruksi_persentase || 0) >= 60
                        ? "warning"
                        : "error"
                    }
                  >
                    {kinerja?.instruksi_persentase || 0}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    ({kinerja?.instruksi_selesai || 0}/
                    {kinerja?.instruksi_diterima || 0})
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox
                p={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(138, 43, 226, 0.1)" }}
              >
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={0.5}
                >
                  Event yang Dibuat
                </MDTypography>
                <MDBox display="flex" flexDirection="column" gap={0.5}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Dibuat:
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontWeight="bold"
                      color="info"
                    >
                      {kinerja?.jumlah_event_dibuat || 0}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Tamu:
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontWeight="bold"
                      color="warning"
                    >
                      {kinerja?.jumlah_tamu_event || 0}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox
                p={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(40, 167, 69, 0.1)" }}
              >
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={0.5}
                >
                  Event yang Dihadiri
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    color="success"
                  >
                    {kinerja?.jumlah_event_dihadiri || 0}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    event
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

export default DetailKinerjaBidang;
